import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import * as dayjs from "dayjs";
import * as utc from "dayjs/plugin/utc";
import { MONTHS_ENUM } from "../../../common/Constants";
import { useGenerateBulkToDoAuditReport, useGetToDoVendorAuditCounts, useGetUserVendorCategoriesByLocations, useGetUserVendorsByCategoryandLocationIds, useGetVendorHeirarchyRecords } from "../../../../backend/masters";
import { ResponseModel } from "../../../../models/responseModel";
import { toast } from "react-toastify";
import { getUserDetails } from "../../../../backend/auth";
import { AxiosError } from "axios";
import PageLoader from "../../../shared/PageLoader";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import './VendorAuditBulkDownloadModal.css';
dayjs.extend(utc as any);

interface MonthYear {
    month: string;
    year: number;
}

function VendorAuditBulkDownloadModal({ onSubmit, onCancel, previousFilters }: any) {
    const [filter, setFilter] = useState<any>({});
    const [company, setCompany] = useState<any>(null);
    const [associateCompany, setAssociateCompany] = useState<any>(null);
    const [associateCompanies, setAssociateCompanies] = useState<any>(null);
    const [vendorLocations, setVendorLocations] = useState<any>(null);
    const [locations, setLocations] = useState<any>(null);
    const [selectedMonthYears, setSelectedMonthYears] = useState<MonthYear[]>([]);
    const [pickerRange, setPickerRange] = useState<any[]>([]);
    const [vendorCategories, setVendorCategories] = useState<any>(null);
    const [vendorRegistrations, setVendorRegistrations] = useState<any>(null);
    const [auditCounts, setAuditCounts] = useState<any>(null);
    const [isOpen, setIsOpen] = useState(false);
    const autoFetchTimerRef = useRef<any>(null);
    const user = getUserDetails();

    const { companies, isFetching }: any = useGetVendorHeirarchyRecords({
        filters: { user_id_eq: user.user_id, is_active_eq: true },
    });

    const { vendorCategoriesByLocations, isFetching: fetchingVendorCategories } =
        useGetUserVendorCategoriesByLocations(
            {
                company_id: (company || {}).value,
                associate_company_id: (associateCompany || {}).value,
                location_ids: (locations || []).map((l: any) => (l || {}).value),
            },
            !!(company || {}).value && !!(associateCompany || {}).value ||
            !!(locations || []).map((l: any) => (l || {}).value).length
        );

    const { getvendorsByCategoryAndLocationIds, isFetching: fetchingVendors } =
        useGetUserVendorsByCategoryandLocationIds(
            {
                company_id: (company || {}).value,
                associate_company_id: (associateCompany || {}).value,
                location_ids: (locations || []).map((l: any) => (l || {}).value),
                vendor_category_ids: (vendorCategories || []).map((vc: any) => (vc || {}).value),
            },
            !!(company || {}).value && !!(associateCompany || {}).value ||
            (!!(locations || []).map((l: any) => (l || {}).value).length &&
             !!(vendorCategories || []).map((vc: any) => (vc || {}).value).length)
        );

    const { getToDoVendorAuditCounts, fetchingAuditCounts } =
        useGetToDoVendorAuditCounts((response: ResponseModel) => {
            if (response instanceof AxiosError) {
                const data = (((response || {}).response || {}).data || {}).errors;
                toast.error(Object.values(data || {}).join(", "));
            } else {
                setAuditCounts(response.data);
            }
        }, errorCallback);

    const { generateBulkToDoAuditReport, generatingAuditReport } =
        useGenerateBulkToDoAuditReport((response: ResponseModel) => {
            if (response instanceof AxiosError) {
                const data = (((response || {}).response || {}).data || {}).errors;
                toast.error(Object.values(data || {}).join(", "));
            } else {
                toast.success("Preparing your ZIP. You will receive an e-mail when it's ready.");
                onSubmit();
                closeDrawer();
            }
        }, errorCallback);

    useEffect(() => {
        requestAnimationFrame(() => setIsOpen(true));
    }, []);

    useEffect(() => {
        if (!previousFilters) return;
        setCompany(previousFilters.company || null);
        setAssociateCompany(previousFilters.associateCompany || null);
        setLocations(previousFilters.locations || null);
        setVendorCategories(previousFilters.vendorCategories || null);
        setVendorRegistrations(previousFilters.vendorRegistrations || null);
        if (previousFilters.company && companies && companies.length) {
            const _company = companies.find((c: any) => c.id == previousFilters.company.value);
            if (_company) setAssociateCompanies(_company.associate_companies);
        }
        if (previousFilters.associateCompany && companies && companies.length) {
            const _company = companies.find((c: any) => c.id == (previousFilters.company || {}).value);
            const _ac = _company && _company.associate_companies && _company.associate_companies.find((c: any) => c.id == previousFilters.associateCompany.value);
            if (_ac) setVendorLocations(_ac.locations);
        }
    }, [previousFilters]);

    // Auto-fetch counts whenever required filters are set
    useEffect(() => {
        if (!company || !associateCompany || selectedMonthYears.length === 0) {
            setAuditCounts(null);
            return;
        }
        if (autoFetchTimerRef.current) clearTimeout(autoFetchTimerRef.current);
        autoFetchTimerRef.current = setTimeout(() => {
            getToDoVendorAuditCounts(buildPayload() as any);
        }, 400);
        return () => {
            if (autoFetchTimerRef.current) clearTimeout(autoFetchTimerRef.current);
        };
    }, [company, associateCompany, selectedMonthYears, locations, vendorCategories, vendorRegistrations]); // eslint-disable-line react-hooks/exhaustive-deps

    function errorCallback() {
        toast.error(ERROR_MESSAGES.DEFAULT);
    }

    function closeDrawer() {
        setIsOpen(false);
        setTimeout(() => onCancel(), 280);
    }

    function onCompanyChange(e: any) {
        if (company && company.value == e.value) return;
        const _company = companies.find((c: any) => c.id == e.value);
        setAssociateCompanies(_company ? _company.associate_companies : null);
        setCompany(e);
        setAssociateCompany(null);
        setLocations(null);
        setSelectedMonthYears([]);
        setPickerRange([]);
        setVendorCategories(null);
        setVendorRegistrations(null);
        setFilter({ company: e });
    }

    function onAssociateCompanyChange(e: any) {
        if (associateCompany && associateCompany.value == e.value) return;
        const _company = companies.find((c: any) => c.id == company.value);
        const _ac = _company && _company.associate_companies && _company.associate_companies.find((c: any) => c.id == e.value);
        setVendorLocations(_ac ? _ac.locations : null);
        setAssociateCompany(e);
        setLocations(null);
        setVendorCategories(null);
        setVendorRegistrations(null);
        setFilter({ ...filter, associateCompany: e, locations: null, vendorCategories: null, vendorRegistrations: null });
    }

    function expandMonthRange(start: any, end: any): MonthYear[] {
        const result: MonthYear[] = [];
        let m = start.month.number;
        let y = start.year;
        const endM = end.month.number;
        const endY = end.year;
        while (y < endY || (y === endY && m <= endM)) {
            result.push({ month: MONTHS_ENUM[m - 1], year: y });
            m++;
            if (m > 12) { m = 1; y++; }
        }
        return result;
    }

    function onLocationsChange(e: any) {
        setLocations(e);
        setVendorCategories(null);
        setVendorRegistrations(null);
        setFilter({ ...filter, locations: e, vendorCategories: null, vendorRegistrations: null });
    }

    function onVendorCategoryChange(e: any) {
        setVendorCategories(e);
        setVendorRegistrations(null);
        setFilter({ ...filter, vendorCategories: e, vendorRegistrations: null });
    }

    function onVendorRegistrationChange(e: any) {
        setVendorRegistrations(e);
        setFilter({ ...filter, vendorRegistrations: e });
    }

    function buildPayload() {
        const payload: any = {
            month_years: selectedMonthYears,
            location_ids: (locations || []).map((l: any) => (l || {}).value),
            vendor_category_ids: (vendorCategories || []).map((l: any) => (l || {}).value),
            vendor_registration_ids: (vendorRegistrations || []).map((l: any) => (l || {}).value),
            auditor_id: getUserDetails().userid,
        };
        if ((company || {}).value) payload.company_id = company.value;
        if ((associateCompany || {}).value) payload.associate_company_id = associateCompany.value;
        // backward compat: single month also sends flat month/year fields
        if (selectedMonthYears.length === 1) {
            payload.month = selectedMonthYears[0].month;
            payload.year = String(selectedMonthYears[0].year);
        }
        return payload;
    }

    function download() {
        if (!company || !associateCompany || selectedMonthYears.length === 0) {
            toast.error("Please select Company, Associate Company and at least one Month–Year.");
            return;
        }
        generateBulkToDoAuditReport(buildPayload() as any);
    }

    const canDownload = auditCounts &&
        auditCounts.completed_count > 0 &&
        !auditCounts.exceeds_download_limit;

    return (
        <>
            <div className={`bulk-drawer-backdrop${isOpen ? ' open' : ''}`} />

            <div className={`bulk-drawer${isOpen ? ' open' : ''}`} role="dialog" aria-modal="true">

                {/* ── Head ── */}
                <div className="la-drawer-head">
                    <div className="la-drawer-title">Download Bulk Report</div>
                    <button className="la-drawer-close" onClick={closeDrawer} aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* ── Body (single scrollable zone) ── */}
                <div className="la-drawer-body">
                    <div className="la-drawer-field">
                        <label>Company Name</label>
                        <Select
                            options={(companies || []).map((x: any) => ({ value: x.id, label: x.name }))}
                            value={company}
                            onChange={onCompanyChange}
                            placeholder="Select company"
                            classNamePrefix="drawer-select"
                            isLoading={isFetching}
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Associate Company</label>
                        <Select
                            options={Array.isArray(associateCompanies) ? associateCompanies.map((x: any) => ({ value: x.id, label: x.name })) : []}
                            value={associateCompany}
                            onChange={onAssociateCompanyChange}
                            placeholder="Select associate company"
                            classNamePrefix="drawer-select"
                            isDisabled={!company}
                            isLoading={isFetching}
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Audit Month(s) and Year(s)</label>
                        <DatePicker
                            onlyMonthPicker
                            range
                            portal
                            zIndex={11200}
                            value={pickerRange}
                            onChange={(dates: any) => {
                                const arr = Array.isArray(dates) ? dates : (dates ? [dates] : []);
                                setPickerRange(arr);
                                if (arr.length === 2 && arr[0] && arr[1]) {
                                    setSelectedMonthYears(expandMonthRange(arr[0], arr[1]));
                                } else {
                                    setSelectedMonthYears([]);
                                }
                            }}
                            format="MMM, YYYY"
                            placeholder="Select month range"
                            disabled={!associateCompany}
                            editable={false}
                            inputClass="bulk-month-picker-input"
                        />
                    </div>

                    {fetchingAuditCounts && (
                        <div className="bulk-counts-loading">Fetching report counts…</div>
                    )}
                    {auditCounts && !fetchingAuditCounts && (
                        <>
                            <div className="bulk-audit-counts">
                                <div className="bulk-count-item">
                                    <div className="bulk-count-value">{auditCounts.completed_count}</div>
                                    <div className="bulk-count-label">Completed Audits</div>
                                </div>
                                <div className="bulk-count-divider" />
                                <div className="bulk-count-item">
                                    <div className="bulk-count-value">{auditCounts.incomplete_count}</div>
                                    <div className="bulk-count-label">Incomplete Audits</div>
                                </div>
                            </div>
                            {auditCounts.exceeds_download_limit && (
                                <div className="bulk-error-msg">
                                    Too many reports ({auditCounts.completed_count}). Max {auditCounts.download_limit} allowed.
                                </div>
                            )}
                            {auditCounts.completed_count === 0 && (
                                <div className="bulk-error-msg">
                                    Cannot generate reports as there are 0 completed audits.
                                </div>
                            )}
                        </>
                    )}

                    <div className="la-drawer-field">
                        <label>Locations</label>
                        <Select
                            options={Array.isArray(vendorLocations) ? vendorLocations.map((x: any) => ({ value: x.id, label: x.name })) : []}
                            value={locations}
                            onChange={onLocationsChange}
                            placeholder="Select location"
                            classNamePrefix="drawer-select"
                            isDisabled={!associateCompany}
                            isLoading={isFetching}
                            isMulti
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Vendor Categories</label>
                        <Select
                            options={(vendorCategoriesByLocations || []).map((x: any) => ({ value: x.id, label: x.name }))}
                            value={vendorCategories}
                            onChange={onVendorCategoryChange}
                            placeholder="Select category"
                            classNamePrefix="drawer-select"
                            isDisabled={!locations || (locations || []).length === 0}
                            isLoading={fetchingVendorCategories}
                            isMulti
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Vendors</label>
                        <Select
                            options={Array.isArray(getvendorsByCategoryAndLocationIds) ? getvendorsByCategoryAndLocationIds.map((x: any) => ({ value: x.id, label: x.vendor_name })) : []}
                            value={vendorRegistrations}
                            onChange={onVendorRegistrationChange}
                            placeholder="Select vendor"
                            classNamePrefix="drawer-select"
                            isDisabled={!vendorCategories || (vendorCategories || []).length === 0}
                            isLoading={fetchingVendors}
                            isMulti
                        />
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="la-drawer-foot">
                    <button
                        className="la-drawer-download-btn"
                        onClick={download}
                        disabled={!canDownload || fetchingAuditCounts}
                    >
                        {canDownload
                            ? `Download Bulk Report (${auditCounts.completed_count} reports)`
                            : "Download Bulk Report"}
                    </button>
                </div>
            </div>

            {generatingAuditReport && <PageLoader />}
        </>
    );
}

export default VendorAuditBulkDownloadModal;
